import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { Notice } from "../components/Notice";
import { QueryErrorState, TabHeading, TabSkeleton } from "../components/tab-states";
import type { MachineCheckFinding } from "../data/cockpit-data";
import {
  useApproveCheckFinding,
  useCheckFindings,
  useChecks,
  useDismissCheckFinding,
} from "../data/queries";

const STATE_LABEL: Record<string, string> = {
  open: "Open",
  awaiting_signoff: "Awaiting sign-off",
  unactionable: "Unactionable",
  failed: "Last action failed",
};

function detailLine(finding: MachineCheckFinding): string {
  const detail = finding.detail as Record<string, unknown>;
  const parts = [
    typeof detail.theme_type === "string" ? detail.theme_type : null,
    typeof detail.campaign_id === "string" && detail.campaign_id ? `campaign ${detail.campaign_id}` : null,
    typeof detail.theme_id === "string" && detail.theme_id ? `theme ${detail.theme_id}` : null,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : finding.subject_ref;
}

function money(finding: MachineCheckFinding): string | null {
  if (finding.money_gbp === null || finding.money_currency === null) return null;
  const native = finding.money_native === null ? "" : ` (${finding.money_native} ${finding.money_currency})`;
  return `£${finding.money_gbp.toFixed(2)}${native}`;
}

/**
 * One finding, and the two things to do with it.
 *
 * Approve executes the fix on Amazon SYNCHRONOUSLY, so the button shows a real
 * pending state and the real outcome — never an optimistic tick. And once a
 * finding is settled the row stops offering the actions that settled it:
 * leaving them live reads as "nothing happened", invites a second click, and
 * the server then correctly refuses it — so the operator's reward for a working
 * system was an error message.
 */
function FindingRow({ finding }: { finding: MachineCheckFinding }) {
  const approve = useApproveCheckFinding();
  const dismiss = useDismissCheckFinding();
  const busy = approve.isPending || dismiss.isPending;
  const failure = approve.error ?? dismiss.error;
  const unactionable = finding.state === "unactionable";
  const spend = money(finding);

  const settled = approve.isSuccess
    ? approve.data?.already_settled
      ? { label: "Already approved", detail: "Someone had already actioned this finding." }
      : { label: "Approved", detail: "Paused on Amazon and recorded in the action ledger." }
    : dismiss.isSuccess
      ? dismiss.data?.already_settled
        ? { label: "Already settled", detail: "Someone had already closed this finding." }
        : { label: "Dismissed", detail: "Closed without touching Amazon." }
      : null;

  return (
    <li>
      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-ks-ink">
                {finding.market} · {settled ? settled.label : (STATE_LABEL[finding.state] ?? finding.state)}
                {spend && <span className="ml-2 font-normal tabular-nums">{spend}</span>}
              </p>
              <p className="mt-1 break-words text-[13px] text-muted-foreground">{detailLine(finding)}</p>
              {unactionable && typeof finding.detail.unactionable_reason === "string" && (
                <p className="mt-1 text-[12.5px] text-muted-foreground">
                  {finding.detail.unactionable_reason}
                </p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                first seen {finding.first_seen_at ?? "unknown"} · last seen {finding.last_seen_at ?? "unknown"}
              </p>
            </div>
            {settled ? (
              <p className="shrink-0 text-[13px] font-semibold text-ks-ink" role="status">
                {settled.label}
              </p>
            ) : (
              <div className="flex shrink-0 gap-2">
                <Button
                  size="lg"
                  disabled={busy || unactionable}
                  title={unactionable ? "Amazon refuses to update this target — nothing to approve." : undefined}
                  onClick={() => approve.mutate(finding.finding_id)}
                >
                  {approve.isPending ? "Applying…" : "Approve"}
                </Button>
                <Button variant="outline" size="lg" disabled={busy} onClick={() => dismiss.mutate(finding.finding_id)}>
                  {dismiss.isPending ? "Dismissing…" : "Dismiss"}
                </Button>
              </div>
            )}
          </div>
          {settled && <p className="text-[13px] text-muted-foreground">{settled.detail}</p>}
          {!settled && failure && (
            <p className="text-[13px] font-medium text-ks-danger" role="alert">
              {failure.message}
            </p>
          )}
        </CardContent>
      </Card>
    </li>
  );
}

/** The weekly checks that watch the Amazon account, and what they found. */
export function ChecksTab() {
  const checks = useChecks();
  const findings = useCheckFindings();
  const [showAll, setShowAll] = useState(false);

  const lastRun = checks.data?.last_run ?? null;
  const rows = findings.data?.findings ?? [];
  const visible = showAll ? rows : rows.filter((row) => row.state !== "unactionable");

  return (
    <div className="flex flex-col gap-4">
      <TabHeading
        title="Checks"
        description="The weekly checks that watch for known-bad conditions on the Amazon account, and what they found."
        aside={
          lastRun
            ? `last run ${lastRun.status}${lastRun.dry_run ? " · dry-run" : ""} · ${lastRun.finished_at ?? lastRun.started_at ?? ""}`
            : undefined
        }
      />

      {/* A run that happened but told nobody is a silent failure of the whole
          point of a weekly check, so it is stated at the top of the tab. */}
      {lastRun?.digest_error && (
        <Notice title="The weekly digest did not send">
          {lastRun.digest_error} — the run happened, but nobody was told.
        </Notice>
      )}
      {checks.data?.config_error && (
        <Notice title="Check configuration unreadable">
          {checks.data.config_error} — checks ran on their code defaults.
        </Notice>
      )}

      {checks.isPending ? null : checks.isError ? (
        <QueryErrorState title="Checks unavailable" onRetry={() => checks.refetch()} />
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {(checks.data?.checks ?? []).map((check) => (
            <li key={check.check_key}>
              <Card>
                <CardContent className="py-3">
                  <p className="text-[13px] font-semibold text-ks-ink">{check.title}</p>
                  <p className="mt-1 text-[12.5px] text-muted-foreground">
                    {check.enabled ? check.mode : "disabled"} · {check.severity}
                    {check.auto_eligible ? "" : " · manual only"}
                    {check.configured ? "" : " · code defaults"}
                  </p>
                  {check.auto_refused && (
                    <p className="mt-1 text-[12.5px] text-muted-foreground">
                      Configured as auto, but this check may not act unattended — running as sign-off.
                    </p>
                  )}
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {findings.isPending ? (
        <TabSkeleton />
      ) : findings.isError ? (
        <QueryErrorState title="Findings unavailable" onRetry={() => findings.refetch()} />
      ) : rows.length === 0 ? (
        <Notice title="Nothing to action">
          The last run found no live findings. Nothing is inferred and no mock data is shown.
        </Notice>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-muted-foreground">
              {visible.length} of {findings.data?.total ?? rows.length} finding(s)
            </p>
            <Button variant="link" size="sm" onClick={() => setShowAll((value) => !value)}>
              {showAll ? "Hide unactionable" : "Show unactionable"}
            </Button>
          </div>
          <ul className="flex flex-col gap-3">
            {visible.map((finding) => (
              <FindingRow key={finding.finding_id} finding={finding} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
